import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { getCurrentTenantId } from '../services/api';
import { useCreches } from '../hooks/useCreches';
import {
  formatCep,
  formatCpf,
  isValidCep,
  isValidCpf,
  isValidEmail,
  isValidPhone,
  isValidUf,
  normalizeCep,
} from '../common/validators';

interface Usuario {
  id: number;
  id_creche: number;
  nome: string;
  cpf: string;
  rg_numero: string;
  rg_uf: string;
  rg_orgao_emissor: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  email: string;
  celular: string;
  whatsapp: string;
  tipo_usuario: string;
  senha?: string;
  confirmarSenha?: string;
  ativo: boolean;
}

export default function UsuariosPage() {
  const { options, creches } = useCreches();
  const [buscandoCep, setBuscandoCep] = useState(false);
  // Com tenant ativo (admin de creche), a creche é implícita e o TabelaCrud a força
  // via `withTenant` — o seletor fica oculto. Sem tenant (admin de sistema operando
  // global), o seletor precisa aparecer para escolher/realocar a creche do usuário.
  const tenantScoped = Boolean(getCurrentTenantId());

  function nomeCrecheById(id: number) {
    return creches.find((c) => c.id === id)?.nome || `ID ${id}`;
  }

  async function buscarCep(item: Partial<Usuario>, onChange: (campo: keyof Usuario, valor: any) => void) {
    const cep = normalizeCep(item.cep || '');
    if (cep.length !== 8) return;

    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();

      if (data.erro) {
        alert('CEP não encontrado.');
        return;
      }

      onChange('logradouro', data.logradouro || '');
      onChange('bairro', data.bairro || '');
      onChange('cidade', data.localidade || '');
      onChange('estado', data.uf || '');
    } catch {
      alert('Não foi possível consultar o CEP no momento.');
    } finally {
      setBuscandoCep(false);
    }
  }

  return (
    <TabelaCrud<Usuario>
      titulo="Usuários do Sistema"
      endpoint="/users"
      colunas={[
        { chave: 'id',     label: 'ID' },
        { chave: 'id_creche', label: 'Creche', render: (item) => nomeCrecheById(item.id_creche) },
        { chave: 'nome',   label: 'Nome' },
        { chave: 'cpf',    label: 'CPF' },
        { chave: 'email',  label: 'E-mail' },
        { chave: 'celular', label: 'Celular' },
        { chave: 'tipo_usuario', label: 'Perfil' },
        {
          chave: 'ativo',
          label: 'Ativo',
          render: (u) => (
            <span className={`badge badge-${u.ativo ? 'pago' : 'vencido'}`}>
              {u.ativo ? 'Sim' : 'Não'}
            </span>
          ),
        },
      ]}
      itemVazio={{
        id_creche: 0,
        nome: '',
        cpf: '',
        rg_numero: '',
        rg_uf: '',
        rg_orgao_emissor: 'SSP',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        email: '',
        celular: '',
        whatsapp: '',
        tipo_usuario: 'administrativo',
        senha: '',
        confirmarSenha: '',
        ativo: true,
      }}
      validateItem={(item) => {
        const criando = !item.id;
        if (!item.id_creche || Number(item.id_creche) <= 0) return 'Selecione a Creche do usuário.';
        if (!item.nome?.trim()) return 'Nome é obrigatório.';
        if (!isValidCpf(item.cpf || '')) return 'CPF inválido.';
        if (!item.rg_numero?.trim()) return 'RG é obrigatório.';
        if (!isValidUf(item.rg_uf || '')) return 'UF do RG inválida.';
        if (!item.rg_orgao_emissor?.trim()) return 'Órgão emissor do RG é obrigatório.';
        if (!isValidCep(item.cep || '')) return 'CEP inválido.';
        if (!item.logradouro?.trim()) return 'Logradouro é obrigatório.';
        if (!item.numero?.trim()) return 'Número do endereço é obrigatório.';
        if (!item.bairro?.trim()) return 'Bairro é obrigatório.';
        if (!item.cidade?.trim()) return 'Cidade é obrigatória.';
        if (!isValidUf(item.estado || '')) return 'UF do endereço inválida.';
        if (!isValidEmail(item.email || '')) return 'E-mail inválido.';
        if (!isValidPhone(item.celular || '', 'mobile')) return 'Celular inválido.';
        if (!isValidPhone(item.whatsapp || '', 'mobile')) return 'WhatsApp inválido.';
        if (!item.tipo_usuario) return 'Selecione o perfil do usuário.';
        if (criando && !item.senha) return 'Senha é obrigatória.';
        if (item.senha && item.senha.length < 12) return 'Senha deve ter no mínimo 12 caracteres.';
        if (item.senha && item.senha !== item.confirmarSenha) return 'As senhas não conferem.';
        return null;
      }}
      renderFormulario={(item, onChange) => (
        <>
          <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche
            <select value={item.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}>
              <option value={0}>Selecione...</option>
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label>Nome
            <input value={item.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} />
          </label>
          <div className="linha-campos linha-2">
            <label>CPF
              <input
                value={item.cpf ?? ''}
                onChange={(e) => onChange('cpf', formatCpf(e.target.value))}
                placeholder="000.000.000-00"
              />
            </label>
            <label>RG Nº
              <input value={item.rg_numero ?? ''} onChange={(e) => onChange('rg_numero', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>UF (RG)
              <input
                value={item.rg_uf ?? ''}
                maxLength={2}
                onChange={(e) => onChange('rg_uf', e.target.value.toUpperCase())}
                placeholder="SP"
              />
            </label>
            <label>Órgão emissor
              <input
                value={item.rg_orgao_emissor ?? ''}
                onChange={(e) => onChange('rg_orgao_emissor', e.target.value.toUpperCase())}
                placeholder="SSP"
              />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>CEP
              <input
                value={item.cep ?? ''}
                onChange={(e) => onChange('cep', formatCep(e.target.value))}
                onBlur={() => buscarCep(item, onChange)}
                placeholder="00000-000"
              />
            </label>
            <label>
              {buscandoCep ? 'Buscando CEP...' : 'Logradouro'}
              <input value={item.logradouro ?? ''} onChange={(e) => onChange('logradouro', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-3">
            <label>Número
              <input value={item.numero ?? ''} onChange={(e) => onChange('numero', e.target.value)} />
            </label>
            <label>Complemento
              <input value={item.complemento ?? ''} onChange={(e) => onChange('complemento', e.target.value)} />
            </label>
            <label>Bairro
              <input value={item.bairro ?? ''} onChange={(e) => onChange('bairro', e.target.value)} />
            </label>
          </div>
          <div className="linha-campos linha-2">
            <label>Cidade
              <input value={item.cidade ?? ''} onChange={(e) => onChange('cidade', e.target.value)} />
            </label>
            <label>UF
              <input
                value={item.estado ?? ''}
                maxLength={2}
                onChange={(e) => onChange('estado', e.target.value.toUpperCase())}
                placeholder="SP"
              />
            </label>
          </div>
          <label>E-mail
            <input type="email" value={item.email ?? ''} onChange={(e) => onChange('email', e.target.value)} />
          </label>
          <div className="linha-campos linha-2">
            <label>Celular
              <input value={item.celular ?? ''} onChange={(e) => onChange('celular', e.target.value)} />
            </label>
            <label>WhatsApp
              <input value={item.whatsapp ?? ''} onChange={(e) => onChange('whatsapp', e.target.value)} />
            </label>
          </div>
          <label>Perfil
            <select value={item.tipo_usuario ?? 'administrativo'} onChange={(e) => onChange('tipo_usuario', e.target.value)}>
              <option value="gestor">Gestor</option>
              <option value="gestor_assistente">Gestor Assistente</option>
              <option value="professor">Professor</option>
              <option value="administrativo">Administrativo</option>
              <option value="responsavel">Responsável</option>
            </select>
          </label>
          <div className="linha-campos linha-2">
            <label>{item.id ? 'Nova senha (opcional)' : 'Senha'}
              <input
                type="password"
                value={item.senha ?? ''}
                onChange={(e) => onChange('senha', e.target.value)}
                placeholder="Mínimo 12 caracteres"
              />
            </label>
            <label>Confirmar senha
              <input
                type="password"
                value={item.confirmarSenha ?? ''}
                onChange={(e) => onChange('confirmarSenha', e.target.value)}
              />
            </label>
          </div>
          {item.id && <small>Deixe os campos de senha em branco para manter a senha atual.</small>}
        </>
      )}
    />
  );
}

